import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useUser } from '../../contexts/UserContext';
import { useSpinner } from '../../utils/GlobalSpinner';
import { useConfirmationDialog } from '../../utils/ConfirmationDialogContext';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api-client';

export function useTrainingSessions() {
  const [workouts, setWorkouts] = useState<any[]>([]);
  const [selectedWorkouts, setSelectedWorkouts] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [isDialogVisible, setDialogVisible] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [filterOption, setFilterOption] = useState('all');
  const [filteredWorkouts, setFilteredWorkouts] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const intl = useIntl();
  const { setLoading } = useSpinner();
  const navigate = useNavigate();
  const { user, coach } = useUser();
  const { showConfirmationDialog } = useConfirmationDialog();
  const { showToast } = useToast();

  useEffect(() => {
    if (!coach || !user) return;

    const loadAll = async () => {
      setLoading(true);
      try {
        const [workoutsResult, studentsResult] = await Promise.allSettled([
          api.workout.findAllWorkoutTemplatesByCoachId(),
          api.coach.fetchStudents()
        ]);

        if (workoutsResult.status === 'fulfilled' && workoutsResult.value.data) {
          setWorkouts(workoutsResult.value.data);
        } else if (workoutsResult.status === 'rejected') {
          showToast('error', intl.formatMessage({ id: 'coach.delete.error.generic' }), workoutsResult.reason?.message);
        }

        if (studentsResult.status === 'fulfilled' && studentsResult.value.data) {
          setStudents(studentsResult.value.data);
        }
      } finally {
        setLoading(false);
      }
    };

    loadAll();
    // eslint-disable-next-line
  }, [refreshKey, user, coach]);

  useEffect(() => {
    const filterWorkouts = () => {
      if (filterOption === 'all') {
        setFilteredWorkouts(workouts);
      } else if (filterOption === 'general') {
        setFilteredWorkouts(workouts.filter((workout) => workout.clientWorkouts.length === 0));
      } else {
        setFilteredWorkouts(
          workouts.filter((workout) =>
            workout.clientWorkouts.some((cw: any) => cw.clientSubscription.client.id === filterOption)
          )
        );
      }
    };

    filterWorkouts();
    // eslint-disable-next-line
  }, [filterOption, workouts, refreshKey]);

  const handleDelete = async (workoutInstanceTemplateId: number) => {
    showConfirmationDialog({
      message: intl.formatMessage({ id: 'coach.delete.confirmation' }),
      header: intl.formatMessage({ id: 'coach.delete.header' }),
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        setLoading(true);
        try {
          await api.workout.deleteWorkoutPlan(workoutInstanceTemplateId, true);
          setRefreshKey((prev) => prev + 1);
        } catch (error: any) {
          console.error('Error deleting workout plan:', error);
          showToast('error', intl.formatMessage({ id: 'coach.delete.error.generic' }), error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleUnassignFromClient = async (workoutInstanceTemplateId: number) => {
    setLoading(true);
    try {
      if (filterOption !== 'all' && filterOption !== 'general') {
        await api.workout.unassignWorkoutFromClient(Number(filterOption), [workoutInstanceTemplateId]);
        setRefreshKey((prev) => prev + 1);
      } else {
        showToast(
          'error',
          intl.formatMessage({ id: 'coach.unassign.error' }),
          intl.formatMessage({ id: 'coach.unassign.error.detail' })
        );
      }
    } catch (error: any) {
      console.error('Error unassigning workout from client:', error);
      showToast('error', intl.formatMessage({ id: 'coach.unassign.error' }), error.message);
    } finally {
      setLoading(false);
      setSelectedWorkouts([]);
      setSelectedClient(null);
      setDialogVisible(false);
    }
  };

  const handleUnassignAllFromClient = async () => {
    setLoading(true);
    try {
      if (filterOption !== 'all' && filterOption !== 'general') {
        await api.workout.unassignWorkoutFromClient(
          Number(filterOption),
          selectedWorkouts.map((workout) => workout.id)
        );
        setRefreshKey((prev) => prev + 1);
      } else {
        showToast(
          'error',
          intl.formatMessage({ id: 'coach.unassign.error' }),
          intl.formatMessage({ id: 'coach.unassign.error.detail' })
        );
      }
      setSelectedWorkouts([]);
      setSelectedClient(null);
    } catch (error: any) {
      console.error('Error unassigning workout from client:', error);
      showToast('error', intl.formatMessage({ id: 'coach.unassign.error' }), error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToClient = async () => {
    setLoading(true);
    try {
      if (selectedWorkouts.length === 0 || !selectedClient) {
        showToast(
          'error',
          intl.formatMessage({ id: 'coach.assign.error' }),
          intl.formatMessage({ id: 'coach.assign.error.detail' })
        );
        return;
      }

      // La medición de carga subjetiva (RPE/RIR) vive a nivel ejercicio (#14/#23):
      // el backend usa el rpeMethod configurado por ejercicio en el catálogo
      // (workout.service.ts ~L391), permitiendo mezclar RPE/RIR en una asignación.
      await api.workout.assignWorkoutToClient(
        selectedClient.id,
        selectedWorkouts.map((workout) => workout.id)
      );

      setDialogVisible(false);
      setSelectedWorkouts([]);
      setSelectedClient(null);
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      console.error('Error assigning workout to client:', error);
      showToast('error', intl.formatMessage({ id: 'coach.assign.error' }), error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignDialog = (workoutId: number) => {
    setSelectedWorkouts([workouts.find((w) => w.id === workoutId)]);
    setDialogVisible(true);
  };

  // #10 (30/08): se dan de baja los chips por alumno. Filtrar los planes de un
  // alumno era la puerta al flujo de desasignar, que Luciano decidió matar
  // porque no aportaba nada. Quedan "Todos" y "Generales".
  const filterOptions = [
    { label: intl.formatMessage({ id: 'plansPage.filter.all' }), value: 'all' },
    { label: intl.formatMessage({ id: 'plansPage.filter.general' }), value: 'general' }
  ];

  return {
    workouts: filteredWorkouts,
    selectedWorkouts,
    setSelectedWorkouts,
    selectedClient,
    setSelectedClient,
    isDialogVisible,
    setDialogVisible,
    students,
    filterOption,
    setFilterOption,
    filterOptions,
    handleDelete,
    handleUnassignFromClient,
    handleUnassignAllFromClient,
    handleAssignToClient,
    handleOpenAssignDialog,
    navigate,
    refreshKey
  };
}
